import React, { useState, useEffect, useCallback } from 'react';
import { Form, Button, Alert, Spinner, Card, ListGroup } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import '../App.css';

export interface UserInfo {
  userName: string;
  survivorName: string;
  realName: string;
  email: string;
  leagueId: string;
  paymentMethod: string;
  isAdmin?: boolean;
}

interface AuthLoginProps {
  onLoginSuccess: (user: UserInfo) => void;
  cardClassName?: string;
  title?: string;
  subtitle?: string;
}

const AuthLogin: React.FC<AuthLoginProps> = ({ 
  onLoginSuccess,
  cardClassName = "login-glass-card text-white",
  title = "🔥 Enter Tribal Council",
  subtitle = "Sign in with your Survivor Name or Email to view your roster & standings"
}) => {
  const [identifier, setIdentifier] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [step, setStep] = useState<'identify' | 'password' | 'setup'>('identify');
  const [isLoading, setIsLoading] = useState(() => {
    return !!(localStorage.getItem('survivor_saved_id') && localStorage.getItem('survivor_saved_pass'));
  });
  const [error, setError] = useState<string | null>(null);
  const [foundUser, setFoundUser] = useState<{ email: string, name?: string, hasPassword?: boolean } | null>(null);
  const [rememberMe, setRememberMe] = useState(false);
  
  // Autocomplete State
  const [suggestions, setSuggestions] = useState<{name: string, email: string}[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);

  const performLogin = useCallback(async (loginId: string, loginPass: string, save: boolean) => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          identifier: loginId.trim(), 
          password: loginPass 
        }),
      });
      const data = await response.json();

      if (!response.ok) throw new Error(data.message || 'Login failed');

      if (save) {
        localStorage.setItem('survivor_saved_id', loginId.trim());
        localStorage.setItem('survivor_saved_pass', loginPass);
      } else {
        localStorage.removeItem('survivor_saved_id');
        localStorage.removeItem('survivor_saved_pass');
      }

      // Pass the email back to the parent to load entries
      onLoginSuccess(data.user);
    } catch (err: any) {
      setError(err.message || 'Login failed');
      // If auto-login failed (e.g. password changed), clear storage
      if (save && localStorage.getItem('survivor_saved_pass') === loginPass) {
        localStorage.removeItem('survivor_saved_id');
        localStorage.removeItem('survivor_saved_pass');
        setRememberMe(false);
      }
    } finally {
      setIsLoading(false);
    }
  }, [onLoginSuccess]);

  // Auto-login on mount if credentials exist
  useEffect(() => {
    const savedId = localStorage.getItem('survivor_saved_id');
    const savedPass = localStorage.getItem('survivor_saved_pass');
    if (savedId && savedPass) {
      setIdentifier(savedId);
      setRememberMe(true);
      performLogin(savedId, savedPass, true);
    } else {
      setIsLoading(false);
    }
  }, [performLogin]);

  // Debounced Autocomplete Search
  useEffect(() => {
    const timer = setTimeout(async () => {
      if (identifier.length < 2 || step !== 'identify') {
        setSuggestions([]);
        setShowSuggestions(false);
        return;
      }

      try {
        const response = await fetch(`${process.env.REACT_APP_API_URL}/api/users/search?q=${encodeURIComponent(identifier)}&mode=autocomplete`);
        if (response.ok) {
          const data = await response.json();
          setSuggestions(data.matches || []);
          setShowSuggestions(true);
        }
      } catch (err) {
        // Silent fail for autocomplete
        console.error("Autocomplete error", err);
      }
    }, 300); // 300ms debounce

    return () => clearTimeout(timer);
  }, [identifier, step]);

  const handleIdentify = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!identifier.trim()) return;

    setIsLoading(true);
    setError(null);
    setFoundUser(null);
    setShowSuggestions(false); // Hide suggestions on submit
    try {
      // Search for user by email or username
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/users/search?q=${encodeURIComponent(identifier)}`);
      const data = await response.json();

      if (!response.ok) throw new Error(data.message || 'Error searching for user');

      if (data.found && data.user) {
        setFoundUser(data.user);
        if (data.user.hasPassword) {
          setStep('password');
        } else {
          setStep('setup');
        }
      } else {
        setError('No account found matching that Email or Survivor Name.');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to lookup user');
    } finally {
      setIsLoading(false);
    }
  };

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!password) return;
    // If foundUser is set (via search flow), use that email, otherwise use identifier input
    const loginId = foundUser?.email || identifier;
    performLogin(loginId, password, rememberMe);
  };

  const handleSetupPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    setIsLoading(true);
    setError(null);
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/auth/setup-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          identifier: foundUser?.email || identifier, 
          password 
        }),
      });
      const data = await response.json();

      if (!response.ok) throw new Error(data.message || 'Password setup failed');

      onLoginSuccess(data.user);
    } catch (err: any) {
      setError(err.message || 'Failed to set password');
    } finally {
      setIsLoading(false);
    }
  };

  const selectSuggestion = (suggestion: { name: string, email: string }) => {
    setIdentifier(suggestion.email); // Use email as it's the safest identifier
    setSuggestions([]);
    setShowSuggestions(false);
  };

  return (
    <Card className={cardClassName}>
      <Card.Body className="p-4">
        {error && <Alert variant="danger" onClose={() => setError(null)} dismissible>{error}</Alert>}
        
        {isLoading ? (
          <div className="text-center py-4">
             <Spinner animation="border" role="status" variant="warning">
                <span className="visually-hidden">Loading...</span>
             </Spinner>
             <p className="mt-3 text-light" style={{ fontSize: '0.95rem' }}>Checking in to Tribal Council...</p>
          </div>
        ) : !isLoading && (
        <>
        {step === 'identify' && (
           <Form onSubmit={handleIdentify}>
            <div className="text-center mb-3">
              <div className="login-title mb-1">{title}</div>
              {subtitle && <small className="text-light opacity-75">{subtitle}</small>}
            </div>
            <Form.Group className="mb-3 position-relative">
              <Form.Label htmlFor="identifier" className="small text-light fw-bold">Survivor Name or Email</Form.Label>
              <Form.Control
                type="text"
                id="identifier"
                name="identifier"
                autoComplete="username"
                placeholder="e.g. Sandra Diaz-Twine or user@email.com"
                value={identifier}
                onChange={(e) => setIdentifier(e.target.value)}
                autoFocus
                className="login-input rounded-3"
              />
              {showSuggestions && suggestions.length > 0 && (
                <ListGroup className="position-absolute w-100 shadow rounded-3 overflow-hidden" style={{ zIndex: 10, maxHeight: '200px', overflowY: 'auto' }}>
                  {suggestions.map((s) => (
                    <ListGroup.Item 
                      key={s.email} 
                      action 
                      onClick={() => selectSuggestion(s)}
                      className="text-dark py-2"
                    >
                      <strong>{s.name}</strong> <small className="text-muted">({s.email})</small>
                    </ListGroup.Item>
                  ))}
                </ListGroup>
              )}
            </Form.Group>
            <Button variant="primary" type="submit" className="w-100 btn-survivor-primary py-2 rounded-pill">
              Continue to League &rarr;
            </Button>
          </Form>
        )}

        {step === 'password' && (
           <Form onSubmit={handleLogin}>
            <div className="mb-3 text-center">
              <div className="login-title mb-1">Welcome Back!</div>
              <p className="mb-1 text-light"><strong>{foundUser?.name || foundUser?.email}</strong></p>
              <Button variant="link" size="sm" className="p-0 text-warning" onClick={() => { 
                setStep('identify'); 
                setIdentifier(''); 
                setPassword('');
                setFoundUser(null);
              }}>Not you? Switch account</Button>
            </div>
            <Form.Group className="mb-3">
              <Form.Label htmlFor="password" className="small text-light fw-bold">Password</Form.Label>
              <div className="input-group">
                <Form.Control
                  type={showPassword ? 'text' : 'password'}
                  id="password"
                  name="password"
                  autoComplete="current-password"
                  placeholder="Enter your password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  autoFocus
                  className="login-input rounded-start-3"
                />
                <Button 
                  variant="outline-secondary" 
                  className="bg-white border-start-0 text-muted" 
                  type="button" 
                  onClick={() => setShowPassword(!showPassword)}
                  title={showPassword ? "Hide password" : "Show password"}
                >
                  {showPassword ? '🙈' : '👁️'}
                </Button>
              </div>
            </Form.Group>
            <div className="d-flex justify-content-between align-items-center mb-3">
              <Form.Check 
                type="checkbox"
                id="rememberMe"
                name="rememberMe"
                label="Remember me"
                checked={rememberMe}
                onChange={(e) => setRememberMe(e.target.checked)}
                className="text-light small mb-0"
              />
              <Link to="/forgot-password" className="small text-warning text-decoration-none">
                Forgot password?
              </Link>
            </div>
            <Button variant="success" type="submit" className="w-100 btn-survivor-primary py-2 rounded-pill">
              Enter Pool
            </Button>
          </Form>
        )}

        {step === 'setup' && (
          <Form onSubmit={handleSetupPassword}>
            <div className="text-center mb-3">
              <div className="login-title mb-1">Set Up Your Password</div>
              <p className="mb-1 text-light">Welcome, <strong>{foundUser?.name || foundUser?.email}</strong>!</p>
              <small className="text-light opacity-75">Create a password to easily sign in and manage your team.</small>
            </div>
            <Form.Group className="mb-3">
              <Form.Label htmlFor="newPassword" className="small text-light fw-bold">New Password</Form.Label>
              <div className="input-group">
                <Form.Control
                  type={showPassword ? 'text' : 'password'}
                  id="newPassword"
                  name="newPassword"
                  autoComplete="new-password"
                  placeholder="Create a password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  autoFocus
                  className="login-input rounded-start-3"
                />
                <Button 
                  variant="outline-secondary" 
                  className="bg-white border-start-0 text-muted" 
                  type="button" 
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? '🙈' : '👁️'}
                </Button>
              </div>
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label htmlFor="confirmPassword" className="small text-light fw-bold">Confirm Password</Form.Label>
              <Form.Control
                type={showPassword ? 'text' : 'password'}
                id="confirmPassword"
                name="confirmPassword"
                autoComplete="new-password"
                placeholder="Confirm your password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className="login-input rounded-3"
              />
            </Form.Group>
            <Button variant="primary" type="submit" className="w-100 btn-survivor-primary py-2 rounded-pill">
              Set Password & Log In
            </Button>
            <div className="mt-2 text-center">
              <Button variant="link" size="sm" className="text-warning" onClick={() => {
                setStep('identify');
                setFoundUser(null);
              }}>&larr; Back</Button>
            </div>
          </Form>
        )}
        </>
        )}
      </Card.Body>
    </Card>
  );
};

export default AuthLogin;
